/**
 * What happened, and what still wants deciding.
 *
 * The interface kept this knowledge itself and it went wrong in the predictable way:
 * a city finished a building, an event was written to a log nobody was watching, and
 * the player found out forty turns later. Both questions belong in the engine, because
 * both are rules — what counts as noteworthy, and what counts as neglected.
 *
 * Named for the two questions the brief says the player must always be able to answer:
 * "what just happened?" and "what should I consider doing next?"
 */

import type { Content } from '../../content/schema/index.js';
import type { GameEvent } from './commands/types.js';
import { citiesOf } from './cities/index.js';
import { inDisorder } from './cities/economy.js';
import { unlocksOf } from './research/knowledge.js';
import { resourceAccess } from './resources/access.js';
import { describeTerms } from './diplomacy/peace.js';
import type { City, GameState, Id } from './state/types.js';

export type NoticeKind =
  | 'built'
  | 'trained'
  | 'grew'
  | 'shrank'
  | 'disorder'
  | 'sold'
  | 'discovered'
  | 'arrived'
  | 'blocked'
  | 'learned'
  | 'era'
  | 'battle'
  | 'captured'
  | 'war'
  | 'peace'
  | 'defeated'
  | 'wonder'
  | 'borders'
  | 'government'
  | 'diplomacy';

export interface Notice {
  kind: NoticeKind;
  text: string;
  /** A city to jump to, when the notice is about one. */
  cityId?: Id;
  /** A unit to jump to, when it is about one. */
  unitId?: Id;
  /** A technology to show in the tree, when it is about one. */
  techId?: Id;
  /** A civilisation offering peace, when the notice is an offer the player can answer. */
  offerFrom?: Id;
  /** A tile to look at, when the notice is about somewhere rather than something. */
  at?: number;
  /** Worth interrupting for, as opposed to merely worth knowing. */
  important: boolean;
}

/**
 * Turn a turn's events into things worth telling the player, in the order they matter.
 *
 * Only the active civilisation's business: a rival's granary is not news until there is
 * diplomacy to make it so.
 */
export function summariseTurn(state: GameState, events: readonly GameEvent[], civId: Id, content?: Content): Notice[] {
  const notices: Notice[] = [];
  const civName = (id: Id): string => state.civs[id]?.name ?? 'Someone';
  const unitName = (type: Id): string => {
    if (!content) return 'unit';
    try {
      return content.units.get(type).name;
    } catch {
      return 'unit';
    }
  };
  const mineUnit = (unitId: Id): boolean => state.units[unitId]?.owner === civId;
  const ours = (cityId: Id): City | null => {
    const city = state.cities[cityId];
    return city && city.owner === civId ? city : null;
  };

  for (const event of events) {
    switch (event.t) {
      case 'BuildingCompleted': {
        const city = ours(event.cityId);
        if (city) notices.push({ kind: 'built', text: `${city.name} completed a ${event.name}`, cityId: city.id, important: true });
        break;
      }
      case 'WonderCompleted': {
        // World news: everyone hears when a wonder is finished, wherever it stands.
        if (event.civId === civId) {
          notices.push({ kind: 'wonder', text: `${event.cityName} has completed the ${event.name}!`, cityId: event.cityId, important: true });
        } else {
          notices.push({ kind: 'wonder', text: `${civName(event.civId)} has completed the ${event.name}`, important: false });
        }
        break;
      }
      case 'WonderLost': {
        const city = ours(event.cityId);
        if (city) {
          const who = event.by >= 0 ? civName(event.by) : 'Someone';
          const carried = event.stored > 0 ? ` — its ${event.stored} production carries over to what it builds next` : '';
          notices.push({
            kind: 'blocked',
            text: `${who} finished the ${event.name} first; ${city.name} has stopped building it${carried}`,
            cityId: city.id,
            important: true,
          });
        }
        break;
      }
      case 'DealOffered':
        if (event.to === civId) {
          notices.push({
            kind: 'diplomacy',
            text: event.demand
              ? `${civName(event.from)} demands tribute — answer on the Rivals screen`
              : `${civName(event.from)} proposes a deal — answer on the Rivals screen`,
            offerFrom: event.from,
            important: true,
          });
        }
        break;
      case 'DealMade':
        if (event.from === civId || event.to === civId) {
          const other = event.from === civId ? event.to : event.from;
          notices.push({
            kind: 'diplomacy',
            text: event.demand
              ? event.from === civId
                ? `${civName(other)} paid the tribute you demanded`
                : `You paid tribute to ${civName(other)}`
              : `Deal agreed with ${civName(other)}`,
            important: false,
          });
        }
        break;
      case 'DealRefused':
        if (event.to === civId && event.demand) {
          notices.push({ kind: 'diplomacy', text: `${civName(event.by)} refused your demand`, important: false });
        }
        break;
      case 'AllianceMade':
        if (event.a === civId || event.b === civId) {
          const ally = event.a === civId ? event.b : event.a;
          notices.push({ kind: 'war', text: `Allied with ${civName(ally)} against ${civName(event.against)}`, important: true });
        }
        break;
      case 'AllianceEnded':
        if (event.b === civId) {
          notices.push({ kind: 'diplomacy', text: `${civName(event.a)} made a separate peace with ${civName(event.against)} — your alliance is over`, important: true });
        }
        break;
      case 'TreatyBroken':
        notices.push(
          event.by === civId
            ? { kind: 'diplomacy', text: `You broke your treaty with ${civName(event.with)} — every civilisation will trust you less`, important: true }
            : { kind: 'diplomacy', text: `${civName(event.by)} broke its treaty with ${event.with === civId ? 'you' : civName(event.with)}`, important: event.with === civId },
        );
        break;
      case 'PaymentStopped':
        if (event.to === civId) {
          notices.push({
            kind: 'diplomacy',
            text: event.defaulted ? `${civName(event.from)} stopped paying what it owes you` : `${civName(event.from)}'s payments to you are complete`,
            important: event.defaulted,
          });
        } else if (event.from === civId && event.defaulted) {
          notices.push({ kind: 'diplomacy', text: `You could not pay ${civName(event.to)} — your word counts for less now`, important: true });
        }
        break;
      case 'OpenBordersEnded':
        if (event.with === civId) notices.push({ kind: 'diplomacy', text: `${civName(event.by)} has closed its borders to you`, important: true });
        break;
      case 'UnitsExpelled':
        if (event.civId === civId) {
          notices.push({
            kind: 'diplomacy',
            text: `${event.count} unit${event.count === 1 ? '' : 's'} sent home from ${civName(event.from)}'s land`,
            important: false,
          });
        }
        break;
      case 'RevolutionStarted':
        if (event.civId === civId) {
          notices.push({
            kind: 'government',
            text: `Revolution! ${event.turns} turn${event.turns === 1 ? '' : 's'} of anarchy, then ${event.name}`,
            important: true,
          });
        }
        break;
      case 'GovernmentChanged':
        // A rival's government is public knowledge; its envoys could hardly hide it.
        notices.push(
          event.civId === civId
            ? { kind: 'government', text: `Your people now live under ${event.name}`, important: true }
            : { kind: 'government', text: `${civName(event.civId)} has adopted ${event.name}`, important: false },
        );
        break;
      case 'BordersExpanded': {
        const city = ours(event.cityId);
        if (city) notices.push({ kind: 'borders', text: `${city.name}'s culture has pushed its border outward`, cityId: city.id, important: false });
        break;
      }
      case 'UnitCompleted': {
        const city = ours(event.cityId);
        if (city) notices.push({ kind: 'trained', text: `${city.name} trained a ${event.name}`, cityId: city.id, important: true });
        break;
      }
      case 'CityGrew': {
        const city = ours(event.cityId);
        if (city) notices.push({ kind: 'grew', text: `${city.name} grew to ${event.population}`, cityId: city.id, important: false });
        break;
      }
      case 'CityStarved': {
        const city = ours(event.cityId);
        if (city) notices.push({ kind: 'shrank', text: `${city.name} is starving — down to ${event.population}`, cityId: city.id, important: true });
        break;
      }
      case 'CityInDisorder': {
        const city = ours(event.cityId);
        if (city) notices.push({ kind: 'disorder', text: `${city.name} is in disorder and building nothing`, cityId: city.id, important: true });
        break;
      }
      case 'BuildingSold': {
        // Only our own. A rival's treasury troubles are not something this
        // civilisation could possibly know, and reporting them leaks through the fog
        // exactly as surely as drawing their cities would.
        const city = ours(event.cityId);
        if (city) {
          notices.push({
            kind: 'sold',
            text: `Could not pay the bills — sold the ${event.name} in ${city.name}`,
            cityId: city.id,
            important: true,
          });
        }
        break;
      }
      case 'ProductionNeedsResource': {
        const city = ours(event.cityId);
        if (city) notices.push({ kind: 'blocked', text: `${city.name} cannot finish its ${event.item} without ${event.resource}`, cityId: city.id, important: true });
        break;
      }
      case 'ResourceGrantEnded': {
        const name = content?.resources.get(event.resource).name ?? 'a resource';
        if (event.to === civId) {
          notices.push({
            kind: 'diplomacy',
            text: event.lost ? `${civName(event.from)} can no longer supply you with ${name}` : `Your supply of ${name} from ${civName(event.from)} has ended`,
            important: true,
          });
        } else if (event.from === civId) {
          notices.push({ kind: 'diplomacy', text: `You no longer supply ${civName(event.to)} with ${name}`, important: false });
        }
        break;
      }
      case 'ProductionHeld': {
        const city = ours(event.cityId);
        if (city) notices.push({ kind: 'blocked', text: `${city.name} needs more citizens before it can finish a ${event.item}`, cityId: city.id, important: true });
        break;
      }
      case 'UnitOrderCompleted':
        if (mineUnit(event.unitId)) {
          notices.push({ kind: 'arrived', text: 'A unit reached its destination', unitId: event.unitId, important: false });
        }
        break;
      case 'GotoBlocked':
        if (mineUnit(event.unitId)) {
          notices.push({ kind: 'blocked', text: 'A unit could not reach where it was sent', unitId: event.unitId, important: true });
        }
        break;
      case 'CombatResolved': {
        const attacker = `${civName(event.attackerOwner)}'s ${unitName(event.attackerType)}`;
        const ours = unitName(event.defenderType);
        if (event.defenderOwner === civId) {
          notices.push(
            event.attackerWon
              ? { kind: 'battle', text: `${attacker} destroyed your ${ours}`, at: event.at, important: true }
              : {
                  kind: 'battle',
                  text: `Your ${ours} held off ${attacker} (${event.defenderHp} hp left)`,
                  unitId: event.defenderId,
                  important: true,
                },
          );
        } else if (event.attackerOwner === civId) {
          // Our own attacks happen on our turn and are told as they happen; this only
          // fires for a fight a standing order started.
          notices.push({
            kind: 'battle',
            text: event.attackerWon ? `Your ${unitName(event.attackerType)} won a battle` : `Your ${unitName(event.attackerType)} was destroyed attacking`,
            at: event.at,
            important: true,
          });
        }
        break;
      }
      case 'AircraftLost':
        if (event.owner === civId) {
          const why = {
            intercepted: 'was shot down by an enemy fighter',
            antiAir: 'was shot down by anti-aircraft fire',
            shotDown: 'was shot down intercepting an air strike',
            cityFell: 'was destroyed on the ground when its city fell',
          }[event.cause];
          notices.push({ kind: 'battle', text: `Your ${unitName(event.unitType)} ${why}`, at: event.at, important: true });
        }
        break;
      case 'AirIntercepted':
        if (event.interceptorOwner === civId && event.strikerHp > 0) {
          notices.push({
            kind: 'battle',
            text: `Your fighters met ${civName(event.strikerOwner)}'s ${unitName(event.strikerType)} — it flew on with ${event.strikerHp} health`,
            at: event.at,
            important: false,
          });
        }
        break;
      case 'UnitBombarded':
        // Our own bombardments are told as they happen; this is theirs on us.
        if (event.targetOwner === civId) {
          notices.push({
            kind: 'battle',
            text:
              event.damage > 0
                ? `${civName(event.attackerOwner)} bombarded your ${unitName(event.targetType)}: −${event.damage} health (${event.targetHp} left)`
                : `${civName(event.attackerOwner)} bombarded your ${unitName(event.targetType)}, and missed`,
            unitId: event.targetId,
            important: event.damage > 0,
          });
        }
        break;
      case 'CargoLost':
        if (event.owner === civId) {
          notices.push({ kind: 'battle', text: `${event.count} of your units went down with their ship`, important: true });
        }
        break;
      case 'UnitPromoted':
        if (event.owner === civId) {
          notices.push({ kind: 'battle', text: `A ${unitName(state.units[event.unitId]?.type ?? -1)} of yours is now ${event.rank}`, unitId: event.unitId, important: false });
        }
        break;
      case 'UnitCaptured':
        if (event.from === civId) {
          notices.push({ kind: 'captured', text: `${civName(event.to)} captured your ${unitName(event.unitType)}`, important: true });
        }
        break;
      case 'CityCaptured':
        if (event.from === civId) {
          const at = cityTile(state, event.cityId);
          notices.push({ kind: 'captured', text: `${civName(event.to)} has captured ${event.name}!`, ...(at === undefined ? {} : { at }), important: true });
        } else if (event.to === civId) {
          notices.push({ kind: 'captured', text: `You have taken ${event.name}`, cityId: event.cityId, important: true });
        }
        break;
      case 'WarDeclared':
        if (event.on === civId) {
          notices.push({ kind: 'war', text: `${civName(event.by)} has declared war on you!`, important: true });
        }
        break;
      case 'PeaceMade':
        if (event.a === civId || event.b === civId) {
          const other = event.a === civId ? event.b : event.a;
          notices.push({ kind: 'peace', text: `You are at peace with ${civName(other)}`, important: true });
        }
        break;
      case 'PeaceOffered':
        if (event.to === civId) {
          // Terms are spelled out where the offer is answered (ADR-0071).
          const gives = content && event.give.length > 0 ? ` and gives you ${describeTerms(state, content, event.give)}` : '';
          const asks = content && event.get.length > 0 ? `${gives ? ',' : ''} if you give ${describeTerms(state, content, event.get)}` : '';
          notices.push({ kind: 'peace', text: `${civName(event.from)} offers peace${gives}${asks}`, offerFrom: event.from, important: true });
        }
        break;
      case 'CityCeded':
        if (event.from === civId) {
          notices.push({ kind: 'peace', text: `${event.name} passes to ${civName(event.to)} under the peace`, important: true });
        } else if (event.to === civId) {
          notices.push({ kind: 'peace', text: `${event.name} is yours under the peace`, cityId: event.cityId, important: true });
        }
        break;
      case 'PeaceRefused':
        if (event.to === civId) {
          notices.push({ kind: 'peace', text: `${civName(event.by)} refused your offer of peace`, important: false });
        }
        break;
      case 'CivDefeated':
        notices.push({
          kind: 'defeated',
          text: event.civId === civId ? 'Your civilisation has fallen' : `${event.name} has been destroyed`,
          important: true,
        });
        break;
      case 'TechDiscovered':
        // Our own discoveries only: what a rival has learned is not something we could
        // know until there is diplomacy (or espionage) to tell us.
        if (event.civId === civId) {
          notices.push({ kind: 'learned', text: `Your scholars have discovered ${event.name}`, techId: event.tech, important: true });
          // What the discovery shows on the map, and how much of it is ours (ADR-0063).
          if (content) {
            const revealed = unlocksOf(content, event.tech).filter((u) => u.kind === 'resource');
            if (revealed.length > 0) {
              const deposits = resourceAccess(state, content, civId).deposits;
              for (const r of revealed) {
                const ours = deposits.filter((d) => d.resource === r.id);
                const joined = ours.filter((d) => d.connected).length;
                notices.push({
                  kind: 'discovered',
                  text:
                    ours.length === 0
                      ? `${r.name} can now be seen — none lies in your lands`
                      : `${r.name} can now be seen: ${ours.length} in your lands, ${joined === 0 ? 'none' : joined} joined to a city${joined < ours.length ? ' — build roads to the rest' : ''}`,
                  important: true,
                });
              }
            }
          }
        }
        break;
      case 'EraEntered':
        if (event.civId === civId) {
          notices.push({ kind: 'era', text: `Your people enter the ${event.name} era`, important: true });
        }
        break;
      case 'TilesDiscovered':
        if (event.civId === civId && event.count >= 6) {
          notices.push({ kind: 'discovered', text: `${event.count} new tiles discovered`, important: false });
        }
        break;
      default:
        break;
    }
  }

  // Important first, but otherwise in the order they happened — a report that reorders
  // everything is harder to trust than one that mostly reads as a narrative.
  return notices.sort((a, b) => Number(b.important) - Number(a.important));
}

function cityTile(state: GameState, cityId: Id): number | undefined {
  const city = state.cities[cityId];
  return city ? city.row * state.map.width + city.col : undefined;
}

/**
 * Cities with nothing in the queue.
 *
 * A city building nothing is pouring its production away every turn, and it is the one
 * neglect the player cannot see from the map. Cities in disorder are excluded: they are
 * not idle, they are in trouble, and they get their own notice.
 */
export function idleCities(state: GameState, content: Content, civId: Id): City[] {
  return citiesOf(state, civId).filter((city) => city.queue.length === 0 && !inDisorder(state, content, city));
}
